import { randomString } from 'infrastructure/utility/randomString';
import { formEncode } from 'infrastructure/utility/formEncode';
import { base64url } from 'infrastructure/utility/base64url';
import { unique } from 'utility/unique';
import { Optional } from 'utility/optional';
import { Lazy, lazy } from 'utility/Lazy';

export type OIDCTokenSet = {
    accessToken: string;
    expiresAt: number; // [ms from epoch]
    idToken: string;
    refreshToken: string;
    codeVerifier?: string;
}

export class OIDCError extends Error {
}

export class OIDCServerError extends OIDCError {
    readonly status: number;
    readonly body: string;

    constructor(status: number, body: string) {
        super(`OIDC client error code ${status}: ${body}`);
        this.status = status;
        this.body = body;
    }
}

export class OIDCClientError extends OIDCError {
    readonly status: number;
    readonly body: string;

    constructor(status: number, body: string) {
        super(`OIDC client error code ${status}: ${body}`);
        this.status = status;
        this.body = body;
    }
}

export class OIDCCriticalError extends OIDCError {
}

type ConfigurationResponse = {
    issuer: string;
    authorization_endpoint: string;
    token_endpoint: string;
    end_session_endpoint: string;
}

function isConfigurationResponse(x: unknown): x is ConfigurationResponse {
    if (typeof x !== 'object') return false;
    if (x === null) return false;

    const required = new Map([
        ['issuer', { type: 'string', found: false }],
        ['authorization_endpoint', { type: 'string', found: false }],
        ['token_endpoint', { type: 'string', found: false }],
        ['end_session_endpoint', { type: 'string', found: false }],
    ]);

    Object.entries(x).forEach(([key, value]) => {
        const info = required.get(key);
        if (info === undefined) return;

        info.found = typeof value === info.type;
    });

    return [...required.values()].every(x => x.found);
}

async function getOpenIDConfiguration(issuer: string) {
    const url = new URL(issuer);
    url.pathname = `${url.pathname.replace(/\/+$/, '')}.well-known/openid-configuration`;
    const res = await fetch(url.href);
    if (res.status >= 500) {
        throw new OIDCServerError(res.status, res.statusText);
    }
    if (res.status >= 400) {
        throw new OIDCClientError(res.status, res.statusText);
    }
    const json = await res.json();
    if (isConfigurationResponse(json)) {
        return json;
    } else {
        throw new OIDCCriticalError('unexpected server configuration');
    }
}

type GetTokenResponse = {
    access_token: string;
    expires_in: number; // [ms]
    id_token: string;
    refresh_token: string;
}

async function getAccessToken(tokenEndpoint: string, clientId: string, clientSecret: string, authCode: string, codeVerifier: string | undefined, redirectUri: string): Promise<OIDCTokenSet> {
    const params = {
        'client_id': clientId,
        'client_secret': clientSecret,
        'grant_type': 'authorization_code',
        'code': authCode,
        'redirect_uri': redirectUri,
    };
    if (codeVerifier) Object.assign(params, { 'code_verifier': codeVerifier });

    const res = await fetch(tokenEndpoint, {
        method: 'POST',
        body: formEncode(params),
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });

    if (Math.floor(res.status / 100) == 4) {
        throw new OIDCClientError(res.status, await res.text());
    }

    if (Math.floor(res.status / 100) == 5) {
        throw new OIDCServerError(res.status, await res.text());
    }

    const json = await res.json() as GetTokenResponse;
    return {
        accessToken: json.access_token,
        expiresAt: Date.now() + json.expires_in * 1000,
        idToken: json.id_token,
        refreshToken: json.refresh_token,
        codeVerifier: codeVerifier,
    };
}

type RefreshTokenResponse = {
    access_token: string;
    expires_in: number; // [ms]
    id_token: string;
    refresh_token: string;
}

async function refreshAccessToken(tokenEndpoint: string, clientId: string, clientSecret: string, refreshToken: string): Promise<OIDCTokenSet> {
    const params = {
        'client_id': clientId,
        'client_secret': clientSecret,
        'grant_type': 'refresh_token',
        'refresh_token': refreshToken,
    };

    const res = await fetch(tokenEndpoint, {
        method: 'POST',
        body: formEncode(params),
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });

    if (Math.floor(res.status / 100) == 4) {
        throw new OIDCClientError(res.status, await res.text());
    }

    if (Math.floor(res.status / 100) == 5) {
        throw new OIDCServerError(res.status, await res.text());
    }

    const json = await res.json() as RefreshTokenResponse;
    return {
        accessToken: json.access_token,
        expiresAt: Date.now() + json.expires_in * 1000,
        idToken: json.id_token,
        refreshToken: json.refresh_token || refreshToken,
    };
}

type OIDCClientParams = {
    client: {
        id: string;
        secret: string;
    };
    issuer: string;
    endpoint: {
        baseUrl: string;
        path?: {
            auth?: string;
            token?: string;
            endSession?: string;
        };
    };
    callbackUrl: string;
    endSessionCanRedirect: boolean;
    isAutoRefreshEnabled: boolean;
}

export type OIDCClientInit = Optional<OIDCClientParams, 'isAutoRefreshEnabled'>;

export class OIDCClient {
    readonly params: OIDCClientParams;
    private readonly config: Lazy<ConfigurationResponse>;

    private _tokenSet: OIDCTokenSet | null = null;
    private _refreshToken: string | null = null;

    readonly _listeners = new Set<() => void>();

    constructor(params: OIDCClientInit) {
        this.params = {
            isAutoRefreshEnabled: true,
            ...params,
        };
        this.config = lazy(getOpenIDConfiguration(this.params.issuer));
    }

    get redirectUrl() {
        return this.params.callbackUrl;
    }

    enableAutoRefresh(value = true) {
        this.params.isAutoRefreshEnabled = value;

        if (this._tokenSet) {
            this.setTokenSet(this._tokenSet);
        }
    }

    static async preparePKCE() {
        const codeVerifier = randomString(52);

        const encoder = new TextEncoder();
        const bytes = encoder.encode(codeVerifier);
        const hash = await crypto.subtle.digest('SHA-256', bytes);

        const codeChallenge = base64url(String.fromCharCode(...new Uint8Array(hash)));

        const codeChallengeMethod = 'S256';

        return { codeVerifier, codeChallenge, codeChallengeMethod };
    }

    async buildAuthUrl(usePKCE = true, additionalScopes: string[] = []) {
        const state = randomString();
        const nonce = randomString();

        const params = {
            'scope': unique(['openid', 'offline_access', 't4_profile', 'token_introspection'].concat(additionalScopes)).join(' '),
            'response_type': 'code',
            'client_id': this.params.client.id,
            'redirect_uri': this.params.callbackUrl,
            'prompt': 'consent',
            'state': state,
            'nonce': nonce,
        };

        let codeVerifier: string | undefined;
        if (usePKCE) {
            const { codeVerifier: codeVerifier_, codeChallenge, codeChallengeMethod } = await OIDCClient.preparePKCE();
            codeVerifier = codeVerifier_;
            Object.assign(params, { 'code_challenge': codeChallenge, 'code_challenge_method': codeChallengeMethod });
        }

        const query = formEncode(params);

        const endpoint =
            this.params.endpoint.path?.auth
            ?? (await this.config.get()).authorization_endpoint
            ?? 'auth';

        const authUrl = `${this.params.endpoint.baseUrl}${endpoint}?${query}`;

        return { authUrl, state, codeVerifier };
    }

    async buildEndSessionUrl() {
        if (!this.tokenSet) {
            throw new Error('cannot request end_session w/o valid tokens');
        }

        const willCallback = this.params.endSessionCanRedirect;

        const endpoint =
            this.params.endpoint.path?.endSession
            ?? (await this.config.get()).end_session_endpoint
            ?? 'session/end';

        const baseUrl = this.params.endpoint.baseUrl + endpoint;
        const state = randomString();
        const params = {
            'id_token_hint': this.tokenSet.idToken,
            'post_logout_redirect_uri': willCallback ? this.params.callbackUrl : undefined,
            state,
        };
        const endSessionUrl = baseUrl + '?' + formEncode(params);

        return { endSessionUrl, state, willCallback };
    }

    get tokenSet() {
        return this._tokenSet;
    }

    private tokenTimer: number | undefined;

    setTokenSet(tokenSet: OIDCTokenSet) {
        if (this.params.isAutoRefreshEnabled) {
            if (this.tokenTimer) {
                window.clearTimeout(this.tokenTimer);
            }

            const now = Date.now();
            if (tokenSet.expiresAt < now || tokenSet.expiresAt - now < 1 * 60 * 1000) {
                this._setTokenSet(null);
            } else {
                const expiresIn = tokenSet.expiresAt - now;
                this.tokenTimer = window.setTimeout(() => {
                    this._setTokenSet(null);
                }, expiresIn / 2);
                this._setTokenSet(tokenSet);
            }
        } else {
            this._setTokenSet(tokenSet);
        }
    }

    clearTokenSet() {
        if (this.params.isAutoRefreshEnabled) {
            if (this.tokenTimer) {
                window.clearTimeout(this.tokenTimer);
            }

            this._setTokenSet(null);
        } else {
            this._setTokenSet(null);
        }
    }

    private _setTokenSet(tokenSet: OIDCTokenSet | null) {
        this._tokenSet = tokenSet;
        if (tokenSet) {
            this._refreshToken = tokenSet.refreshToken;
        }

        for (const listener of this._listeners) {
            listener();
        }
    }

    addTokenSetListener(listener: () => void) {
        this._listeners.add(listener);
    }

    removeTokenSetListener(listener: () => void) {
        this._listeners.delete(listener);
    }

    async getToken(code: string, codeVerifier: string | undefined) {
        const endpoint =
            this.params.endpoint.path?.token
            ?? (await this.config.get()).token_endpoint
            ?? 'token';
        const url = this.params.endpoint.baseUrl + endpoint;
        try {
            this._refreshToken = null;
            const newTokenSet = await getAccessToken(url, this.params.client.id, this.params.client.secret, code, codeVerifier, this.params.callbackUrl);
            this.setTokenSet(newTokenSet);
            return newTokenSet;
        } catch (e) {
            this._refreshToken = null;
            this._setTokenSet(null);
            throw e;
        }
    }

    async updateToken() {
        if (!this._refreshToken) {
            throw new OIDCCriticalError('attempt to refresh access token w/o valid refresh_token');
        }

        const endpoint =
            this.params.endpoint.path?.token
            ?? (await this.config.get()).token_endpoint
            ?? 'token';

        const url = this.params.endpoint.baseUrl + endpoint;
        const refreshToken = this._refreshToken;
        try {
            const tokenSet = await refreshAccessToken(url, this.params.client.id, this.params.client.secret, refreshToken);
            this.setTokenSet(tokenSet);
            return tokenSet;
        } catch (e) {
            this._refreshToken = null;
            this._setTokenSet(null);
            throw e;
        }
    }

    get refreshable(): boolean {
        return !!this._refreshToken;
    }
}
